import { useEffect, useState } from 'react'
import {
  Box, HStack, VStack, Text, Button, Field, Badge, Icon, NumberInput,
  type NumberInputValueChangeDetails,
} from '@chakra-ui/react'
import { FiPlay, FiTrash2, FiZap } from 'react-icons/fi'
import { useQueryClient } from '@tanstack/react-query'
import { toaster } from '@/components/ui/toaster'
import { useSimulateCampaign, useResetSimulation } from '../../../hooks/useCampaignSimulation'
import { useJobPolling } from '../../../hooks/useJobPolling'

interface Props {
  campaignId: string
}

// Demo-only card (gated by the page via useDemoMode) that lets a demo operator fast-forward
// canvassing activity for this campaign. Follows the slice 6b job-polling rules documented in
// frontend/src/CLAUDE.md: branch on `!isTerminal` alone (never `!isTerminal || !job` -- a 404
// reports isTerminal=true with job undefined), and invalidate campaign caches inside the
// terminal `Succeeded` branch only, never at enqueue time.
export function SimulationPanel({ campaignId }: Props) {
  const [backfillDays, setBackfillDays] = useState(0)
  const [doorBudget, setDoorBudget] = useState(35)
  const [jobId, setJobId] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const simulate = useSimulateCampaign(campaignId)
  const reset = useResetSimulation(campaignId)
  const { data: job, isTerminal } = useJobPolling(jobId)

  const running = !!jobId && !isTerminal

  useEffect(() => {
    if (!isTerminal) return

    if (job?.status === 'Succeeded') {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'stats'] })
      toaster.create({ title: 'Simulation complete', type: 'success', duration: 3000 })
    } else {
      toaster.create({
        title: 'Simulation failed',
        description: job?.errorMessage ?? 'An error occurred.',
        type: 'error',
        duration: 5000,
      })
    }
    setJobId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerminal, job])

  const handleRun = async () => {
    try {
      const { jobId: id } = await simulate.mutateAsync({ backfillDays, doorBudget })
      setJobId(id)
    } catch {
      toaster.create({ title: 'Could not start simulation', type: 'error', duration: 5000 })
    }
  }

  const handleReset = async () => {
    if (!window.confirm('Clear all simulated canvassing data for this campaign?')) return
    try {
      const r = await reset.mutateAsync()
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'stats'] })
      toaster.create({ title: `Cleared ${r.sessionsDeleted} simulated visits`, type: 'success', duration: 3000 })
    } catch {
      toaster.create({ title: 'Reset failed', type: 'error', duration: 5000 })
    }
  }

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md" borderWidth="1px" borderColor="purple.200">
      <HStack gap={3} mb={4}>
        <Icon color="purple.500" asChild><FiZap /></Icon>
        <Text fontWeight="medium">Simulate Canvassing</Text>
        <Badge colorPalette="purple" variant="subtle">Demo</Badge>
      </HStack>
      <VStack align="stretch" gap={4}>
        <HStack gap={4} flexWrap="wrap">
          <Field.Root maxW="200px">
            <Field.Label>Backfill days</Field.Label>
            <NumberInput.Root
              size="sm"
              min={0}
              value={String(backfillDays)}
              onValueChange={(details: NumberInputValueChangeDetails) => setBackfillDays(details.valueAsNumber || 0)}
            >
              <NumberInput.Input />
              <NumberInput.Control>
                <NumberInput.IncrementTrigger />
                <NumberInput.DecrementTrigger />
              </NumberInput.Control>
            </NumberInput.Root>
          </Field.Root>
          <Field.Root maxW="200px">
            <Field.Label>Doors / canvasser / day</Field.Label>
            <NumberInput.Root
              size="sm"
              min={1}
              value={String(doorBudget)}
              onValueChange={(details: NumberInputValueChangeDetails) => setDoorBudget(details.valueAsNumber || 1)}
            >
              <NumberInput.Input />
              <NumberInput.Control>
                <NumberInput.IncrementTrigger />
                <NumberInput.DecrementTrigger />
              </NumberInput.Control>
            </NumberInput.Root>
          </Field.Root>
        </HStack>
        <HStack gap={3}>
          <Button colorPalette="purple" onClick={handleRun} loading={simulate.isPending || running}>
            <FiPlay /> Simulate a day
          </Button>
          <Button variant="outline" colorPalette="red" onClick={handleReset} loading={reset.isPending}>
            <FiTrash2 /> Reset
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
